/** Lib Imports */
import { useState, useEffect, useRef, useCallback } from "react";

/** RTK Query */
import { useLazyGetDataQuery } from "../../../app/services/commonApi";

/** Utilities */
import { encryptUrlData } from "../../../utils/encryption/CryptoService";

/** Components */
import Badges from "../../../components/common/badges/Badges";

/** CSS */
import "./GlobalSearch.scss";

/** Enums */
import { ApplicantStatus } from "../../../enums/ApplicantStatus.ENUM";
import { getAuthProps } from "../../../utils/authentication/authLibrary";
import UserRoles from "../../../enums/UserRoles.ENUM";

const GlobalSearch = () => {
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const [triggerSearch] = useLazyGetDataQuery();

    const handleSearch = useCallback(async (query) => {
        setIsSearching(true);
        setShowResults(true);
        try {
            const response = await triggerSearch({
                url: "/ManageApplicants/SearchApplicant",
                data: { SearchQuery: query }
            }).unwrap();
            setSearchResults(response || []);
        } catch (error) {
            console.error("Search failed:", error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [triggerSearch]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                handleSearch(searchQuery);
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, handleSearch]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            // Ctrl + Q to focus
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'q') {
                event.preventDefault();
                inputRef.current?.focus();
            }
            // Escape to blur and hide results
            if (event.key === 'Escape') {
                inputRef.current?.blur();
                setShowResults(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const user = getAuthProps();
    if(user.roles.roleId === UserRoles.INTERVIEWER) {
        return;
    }
    const handleResultClick = (applicantId,applicantStatusId,applicantResumeId) => {
        if (applicantId) {
            const path =
              applicantStatusId === ApplicantStatus.DRAFT
                ? `/modify-applicant-details/${encryptUrlData(applicantResumeId)}`
                : `/applicant-details/${encryptUrlData(applicantId)}`;
            window.open(path, "_blank");
            setSearchQuery("");
            setShowResults(false);
        }
    };

    return (
        <div className="input-search" ref={dropdownRef}>
            <i className="pi pi-search"></i>
            <input
                ref={inputRef}
                type="text"
                placeholder="Search Candidates"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim().length >= 2 && setShowResults(true)}
            />
            <div className="search-shortcut">
                <kbd>Ctrl + Q</kbd>
            </div>

            {showResults && (
                <div className="search-results-dropdown">
                    {isSearching && (
                        <div className="search-message">
                            <i className="pi pi-spin pi-spinner mr-2"></i> Searching Applicants...
                        </div>
                    )}
                    {!isSearching && searchResults.length > 0 &&(
                        <div className="results-list">
                            {searchResults.map((applicant) => (
                                <button
                                    key={applicant.applicantId}
                                    className="result-item"
                                    onClick={() => handleResultClick(applicant.applicantId,applicant.applicantStatusId,applicant.applicantResumeId)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleResultClick(applicant.applicantId,applicant.applicantStatusId,applicant.applicantResumeId);
                                        }
                                      }}
                                    type="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="result-header">
                                        <div className="applicant-name">
                                            {applicant.firstName} {applicant.lastName}
                                        </div>
                                        <Badges label={applicant.applicantStatusName} type={applicant.applicantStatusName} showDot={false} />
                                    </div>
                                    <div className="result-details">
                                        <div className="applicant-contact">
                                            <div className="applicant-email">
                                                <i className="pi pi-envelope mr-1"></i>
                                                {applicant.applicantEmail}
                                            </div>
                                            <div className="applicant-phone">
                                                <i className="pi pi-phone mr-1"></i>
                                                {applicant.applicantPhone || "N/A"}
                                            </div>
                                        </div>
                                        <div className="applicant-position">
                                            <i className="pi pi-briefcase mr-1"></i>
                                            {applicant.positionName}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    {!isSearching && searchResults.length === 0 && (

                        <div className="search-message">No Applicants found</div>
                    )}
                </div>
            )}
        </div>
    );
};

GlobalSearch.propTypes = {
  // GlobalSearch doesn't currently receive any props
};

export default GlobalSearch;
